import { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Breadcrumbs,
  Button,
  CircularProgress,
  IconButton,
  Link,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Download,
  Folder,
  InsertDriveFile,
  Visibility,
} from "@mui/icons-material";
import { formatBytes } from "components/preview/previewTypes";
import { startDownloadViaHiddenFrame } from "utils/hiddenFrameDownload";
import WorkspaceFileViewer, { type ViewerFile } from "./WorkspaceFileViewer";
import { pushNotification } from "features/notificationSlice";
import { useAppDispatch } from "features/store";
import {
  useGetPdfJobFilesQuery,
  useLazyGetPdfJobFileUrlQuery,
  useLazyGetPdfJobFilesQuery,
  type PdfWorkspaceItem,
  type PdfWorkspaceSection,
} from "features/pdfTools/pdfTools";

const SECTION_TABS: { key: PdfWorkspaceSection; label: string; hint: string }[] = [
  {
    key: "input",
    label: "Input PDFs",
    hint: "Source PDFs the tool extracted from the uploaded archive.",
  },
  {
    key: "result",
    label: "Result",
    hint: "One folder per employee with the renamed files.",
  },
  {
    key: "error_pages",
    label: "Error pages",
    hint: "Pages the tool could not match to an employee (missing name, SSN or year). They are not included in any employee folder - review them manually.",
  },
];

interface JobWorkspacePanelProps {
  jobId: string;
  /** Refresh listings periodically while the job is running. */
  live: boolean;
  /** The section to open on (Error pages from the page's alert). */
  initialSection?: PdfWorkspaceSection;
}

function mergeByPath(
  first: PdfWorkspaceItem[],
  more: PdfWorkspaceItem[],
): PdfWorkspaceItem[] {
  const seen = new Set(first.map((item) => `${item.type}:${item.path}`));
  return [
    ...first,
    ...more.filter((item) => !seen.has(`${item.type}:${item.path}`)),
  ];
}

export default function JobWorkspacePanel({
  jobId,
  live,
  initialSection = "result",
}: JobWorkspacePanelProps) {
  const dispatch = useAppDispatch();
  const [section, setSection] = useState<PdfWorkspaceSection>(initialSection);
  const [prefix, setPrefix] = useState("");
  const [extraItems, setExtraItems] = useState<PdfWorkspaceItem[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [viewerIndex, setViewerIndex] = useState<number | null>(null);

  const {
    data: firstPage,
    isFetching,
    refetch,
  } = useGetPdfJobFilesQuery(
    { jobId, section, prefix, limit: 100 },
    { pollingInterval: live ? 8000 : 0, refetchOnMountOrArgChange: true },
  );
  const [fetchMore, { isFetching: fetchingMore }] = useLazyGetPdfJobFilesQuery();
  const [getFileUrl] = useLazyGetPdfJobFileUrlQuery();

  useEffect(() => {
    setSection(initialSection);
  }, [initialSection]);

  // The last files land as the job ends; the polling has stopped by then.
  const wasLiveRef = useRef(live);
  useEffect(() => {
    if (wasLiveRef.current && !live) void refetch();
    wasLiveRef.current = live;
  }, [live, refetch]);

  // Pages loaded with Load more stay while the first page refreshes; only a new
  // place in the workspace starts over.
  useEffect(() => {
    setExtraItems([]);
    setNextCursor(null);
  }, [section, prefix, jobId]);

  useEffect(() => {
    setPrefix("");
  }, [section]);

  const cursor = extraItems.length > 0 ? nextCursor : (firstPage?.next_cursor ?? null);

  const items = useMemo(
    () => mergeByPath(firstPage?.items ?? [], extraItems),
    [firstPage, extraItems],
  );

  const viewerFiles: ViewerFile[] = useMemo(
    () =>
      items
        .filter(
          (item): item is PdfWorkspaceItem & { key: string } =>
            item.type === "file" &&
            !!item.key &&
            item.name.toLowerCase().endsWith(".pdf"),
        )
        .map((item) => ({ name: item.name, key: item.key, size: item.size })),
    [items],
  );

  useEffect(() => {
    setViewerIndex(null);
  }, [section, prefix]);

  const crumbs = prefix.split("/").filter(Boolean);
  const sectionMeta = SECTION_TABS.find((t) => t.key === section);

  const handleLoadMore = async () => {
    if (!cursor) return;
    try {
      const res = await fetchMore({
        jobId,
        section,
        prefix,
        cursor,
        limit: 100,
      }).unwrap();
      setExtraItems((prev) => mergeByPath(prev, res.items));
      setNextCursor(res.next_cursor);
    } catch {
      dispatch(
        pushNotification({
          message: "Could not load more files. Try again.",
          severity: "error",
        }),
      );
    }
  };

  const handlePreview = (item: PdfWorkspaceItem) => {
    const idx = viewerFiles.findIndex((f) => f.key === item.key);
    if (idx >= 0) setViewerIndex(idx);
  };

  const handleDownload = async (item: PdfWorkspaceItem) => {
    if (!item.key) return;
    setBusyKey(item.key);
    try {
      const { url } = await getFileUrl({ jobId, key: item.key }).unwrap();
      startDownloadViaHiddenFrame(url);
    } catch {
      dispatch(
        pushNotification({
          message: `Could not download "${item.name}".`,
          severity: "error",
        }),
      );
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      <Tabs
        value={section}
        onChange={(_e, v) => setSection(v)}
        variant="scrollable"
        allowScrollButtonsMobile
        sx={{ minHeight: 36, "& .MuiTab-root": { minHeight: 36, py: 0.5 } }}
      >
        {SECTION_TABS.map((tab) => (
          <Tab key={tab.key} value={tab.key} label={tab.label} />
        ))}
      </Tabs>

      {sectionMeta && (
        <Typography variant="caption" color="text.secondary">
          {sectionMeta.hint}
          {live ? " The list refreshes automatically while the job runs." : ""}
        </Typography>
      )}

      <Breadcrumbs sx={{ fontSize: 13 }}>
        <Link
          component="button"
          type="button"
          underline="hover"
          onClick={() => setPrefix("")}
          sx={{ fontSize: 13 }}
        >
          {sectionMeta?.label ?? section}
        </Link>
        {crumbs.map((part, idx) => {
          const target = `${crumbs.slice(0, idx + 1).join("/")}/`;
          const isLast = idx === crumbs.length - 1;
          return isLast ? (
            <Typography key={target} sx={{ fontSize: 13 }} color="text.primary">
              {part}
            </Typography>
          ) : (
            <Link
              key={target}
              component="button"
              type="button"
              underline="hover"
              onClick={() => setPrefix(target)}
              sx={{ fontSize: 13 }}
            >
              {part}
            </Link>
          );
        })}
      </Breadcrumbs>

      <Box
        data-inner-scroll="workspace"
        sx={{
          border: 1,
          borderColor: "divider",
          borderRadius: 1,
          overflow: "auto",
          height: { xs: 320, sm: 400, md: 440 },
          bgcolor: "background.paper",
        }}
      >
        {isFetching && items.length === 0 ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={26} />
          </Box>
        ) : items.length === 0 ? (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ p: 2, textAlign: "center" }}
          >
            {live ? "Nothing here yet - files appear as they are produced." : "No files."}
          </Typography>
        ) : (
          items.map((item) => (
            <Box
              key={`${item.type}-${item.path}`}
              onClick={
                item.type === "folder" ? () => setPrefix(item.path) : undefined
              }
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                px: 1.25,
                py: 0.6,
                borderBottom: "1px solid",
                borderColor: "rgba(0,0,0,0.05)",
                cursor: item.type === "folder" ? "pointer" : "default",
                "&:hover": { bgcolor: "action.hover" },
              }}
            >
              {item.type === "folder" ? (
                <Folder sx={{ color: "#f5b942", flexShrink: 0 }} />
              ) : (
                <InsertDriveFile
                  sx={{ color: "text.secondary", flexShrink: 0 }}
                  fontSize="small"
                />
              )}
              <Typography noWrap title={item.name} sx={{ flex: 1, minWidth: 0, fontSize: 14 }}>
                {item.name}
              </Typography>
              {item.type === "file" && (
                <>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ flexShrink: 0 }}
                  >
                    {formatBytes(item.size)}
                  </Typography>
                  {busyKey === item.key ? (
                    <CircularProgress size={16} sx={{ mx: 1 }} />
                  ) : (
                    <>
                      <Tooltip title="Preview">
                        <IconButton
                          size="small"
                          onClick={() => handlePreview(item)}
                        >
                          <Visibility fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Download">
                        <IconButton
                          size="small"
                          onClick={() => void handleDownload(item)}
                        >
                          <Download fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                </>
              )}
            </Box>
          ))
        )}
        {cursor && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
            <Button
              size="small"
              onClick={() => void handleLoadMore()}
              disabled={fetchingMore}
              startIcon={
                fetchingMore ? <CircularProgress size={14} /> : undefined
              }
            >
              Load more
            </Button>
          </Box>
        )}
      </Box>

      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Button size="small" onClick={() => void refetch()}>
          Refresh
        </Button>
      </Box>

      {viewerIndex !== null && viewerFiles[viewerIndex] && (
        <WorkspaceFileViewer
          jobId={jobId}
          files={viewerFiles}
          index={viewerIndex}
          onClose={() => setViewerIndex(null)}
          onIndexChange={setViewerIndex}
        />
      )}
    </Box>
  );
}
